// Admin-only coupon code utilities.
//
// Requirements:
// - 12 characters
// - letters + numbers only
// - always starts with a letter
// - frontend-only convenience (equivalent to typing a code manually)
//
// Bulk create generates codes on the backend; these helpers remain for the
// single-create shuffle button and shared constants/validation.

export const COUPON_CODE_LENGTH = 12;
export const BULK_CREATE_MAX_COUNT = 20;
export const BULK_CREATE_MAX_REDEMPTIONS_EXCLUSIVE = 10;
// Access coupons carry no discount, so a default-access batch creates DB rows only
// (no per-coupon Stripe sync) and can safely run much larger than a bulk discount create.
// Must stay in sync with DEFAULT_ACCESS_COUNT_MAX in admin_affiliate_views.py.
export const DEFAULT_ACCESS_MAX_COUNT = 200;

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const ALPHANUM = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
const DIGITS = "0123456789";

const hasCrypto =
  typeof window !== "undefined" &&
  typeof window.crypto !== "undefined" &&
  typeof window.crypto.getRandomValues === "function";

const cryptoRandomIndex = (maxExclusive) => {
  // Rejection sampling to avoid modulo bias.
  // For maxExclusive <= 36 this is very fast.
  const max = Number(maxExclusive);
  if (!Number.isFinite(max) || max <= 0) throw new Error("maxExclusive must be a positive number");

  const buckets = Math.floor(256 / max);
  const limit = buckets * max;
  const buf = new Uint8Array(1);

  while (true) {
    window.crypto.getRandomValues(buf);
    const x = buf[0];
    if (x < limit) return x % max;
  }
};

const mathRandomIndex = (maxExclusive) => Math.floor(Math.random() * maxExclusive);

const randomIndex = (maxExclusive) => (hasCrypto ? cryptoRandomIndex(maxExclusive) : mathRandomIndex(maxExclusive));

const randomChar = (chars) => chars[randomIndex(chars.length)];

export const generateCouponCode = (length = COUPON_CODE_LENGTH) => {
  const len = Number(length);
  const finalLen = Number.isFinite(len) && len >= 1 ? Math.floor(len) : COUPON_CODE_LENGTH;

  let out = randomChar(LETTERS); // first char must be a letter
  while (out.length < finalLen) out += randomChar(ALPHANUM);
  return out;
};

export const DEFAULT_COUPON_CODE_MAX_LENGTH = 64;

/**
 * Code prefilled for the global "default coupon" template:
 * uppercased username + the template's digits-only suffix (may be empty).
 */
export const buildDefaultCouponCode = (username, suffix) =>
  `${String(username || "").trim().toUpperCase()}${suffix || ""}`.slice(0, DEFAULT_COUPON_CODE_MAX_LENGTH);

/**
 * Generate an alternative default coupon code with "2" added after the username.
 * Used when the primary code collides with an existing coupon.
 * Example: "USERNAME" + suffix "123" → "USERNAME2123"
 */
export const buildAlternativeDefaultCouponCode = (username, suffix) => {
  const cleanUsername = String(username || "").trim().toUpperCase();
  return `${cleanUsername}2${suffix || ""}`.slice(0, DEFAULT_COUPON_CODE_MAX_LENGTH);
};

/** PREFIX + digits filling remaining length up to `length` (mirrors backend bulk rules). */
export const generatePrefixedCouponCode = (prefix, length = COUPON_CODE_LENGTH) => {
  const normalized = String(prefix || "")
    .trim()
    .toUpperCase();
  const finalLen = Number.isFinite(Number(length)) && Number(length) >= 1 ? Math.floor(Number(length)) : COUPON_CODE_LENGTH;
  if (!normalized) throw new Error("prefix is required for prefixed coupon codes.");
  const remaining = finalLen - normalized.length;
  if (remaining < 1) {
    throw new Error(`prefix must be shorter than ${finalLen} characters so digits can be appended.`);
  }
  let out = normalized;
  while (out.length < finalLen) out += randomChar(DIGITS);
  return out;
};

